import { getQuestions } from "../data/businesses";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

export default function Screen2Questionnaire({
  businessType,
  answers,
  onAnswersChange,
  qStep,
  onQStepChange,
  onBack,
  onFinish,
}) {
  const questions = getQuestions(businessType);
  // Clamp qStep to valid range (guards against bad saved state)
  const step = Math.min(Math.max(qStep || 0, 0), Math.max(questions.length - 1, 0));
  const current = questions[step];
  if (!current) return null;

  const answerForCurrent = answers[current.key];
  const isOptional = current.optional === true;
  const canContinue = isOptional
    ? true
    : current.type === "text"
    ? (answerForCurrent || "").trim().length >= 3
    : current.type === "number"
    ? (answerForCurrent || "").toString().trim().length >= 1
    : Boolean(answerForCurrent);

  const progressPct = ((step + 1) / questions.length) * 100;

  const handleAnswer = (value) => {
    onAnswersChange({ ...answers, [current.key]: value });
  };

  const handleNumberChange = (raw) => {
    // Strip every non-digit so the stored value is always a clean integer string.
    // We deliberately do not store leading zeros — that's why the placeholder asks
    // for "solo el número, sin $ ni ceros".
    const digits = (raw || "").replace(/\D/g, "");
    const trimmed = digits.replace(/^0+/, "");
    handleAnswer(trimmed);
  };

  const handleNext = () => {
    if (!canContinue) return;
    if (step < questions.length - 1) {
      onQStepChange(step + 1);
    } else {
      onFinish(answers);
    }
  };

  const handlePrev = () => {
    if (step === 0) {
      onBack();
      return;
    }
    onQStepChange(step - 1);
  };

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-6 pb-6"
      data-testid="screen-2"
    >
      {/* Top bar: back + counter + progress */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={handlePrev}
          data-testid="prev-question-button"
          className="flex items-center gap-1 -ml-2 px-2 py-2 rounded-lg text-slate-600 active:bg-slate-100"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2.2} />
          <span className="text-sm font-medium">Atrás</span>
        </button>
        <span
          className="text-sm font-bold text-slate-500"
          data-testid="progress-counter"
        >
          {step + 1} / {questions.length}
        </span>
      </div>

      <div
        className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-8"
        data-testid="progress-bar"
      >
        <div
          className="h-full bg-teal-600 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Question */}
      <div key={step} className="animate-fade-in-up">
        {current.groupHeader && (
          <p
            className="text-xs font-bold uppercase tracking-wider mb-3"
            style={{ color: "#F97316" }}
            data-testid="question-group-header"
          >
            {current.groupHeader}
          </p>
        )}
        <h2 className="text-2xl sm:text-[1.7rem] font-bold tracking-tight text-slate-900 leading-snug">
          {current.label}
          {isOptional && (
            <span className="text-base font-medium text-slate-400 ml-2">
              (opcional)
            </span>
          )}
        </h2>
        <p className="text-xs text-slate-400 mt-2">{current.why}</p>

        {/* Options OR text input OR number input */}
        <div className="mt-6 flex flex-col gap-3">
          {current.type === "text" ? (
            <textarea
              value={answerForCurrent || ""}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder={current.placeholder || "Escribe aquí..."}
              rows={3}
              maxLength={current.maxLength || 200}
              data-testid="question-input"
              className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400 resize-none"
            />
          ) : current.type === "number" ? (
            <>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={answerForCurrent || ""}
                onChange={(e) => handleNumberChange(e.target.value)}
                placeholder={current.placeholder || "Solo el número"}
                data-testid="question-number-input"
                className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400"
              />
              {current.helper && (
                <p
                  className="text-xs text-slate-500 leading-snug -mt-1"
                  data-testid="question-helper"
                >
                  {current.helper}
                </p>
              )}
            </>
          ) : (
            current.options.map((opt) => {
              const isSelected = answerForCurrent === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleAnswer(opt)}
                  data-testid={`option-${opt}`}
                  className={[
                    "flex items-center justify-between text-left p-4 rounded-xl border-2 transition-all duration-150 active:scale-[0.99]",
                    isSelected
                      ? "border-teal-600 bg-teal-50"
                      : "border-slate-200 bg-white active:bg-slate-50",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "text-base font-semibold leading-snug",
                      isSelected ? "text-teal-900" : "text-slate-800",
                    ].join(" ")}
                  >
                    {opt}
                  </span>
                  {isSelected && (
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-600 text-white shrink-0 ml-3">
                      <Check className="w-4 h-4" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="flex-1 min-h-[2rem]" />

      {/* CTA */}
      <div className="pt-6 pb-safe">
        <button
          type="button"
          disabled={!canContinue}
          onClick={handleNext}
          data-testid="next-question-button"
          className={[
            "w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all duration-200",
            canContinue
              ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600"
              : "bg-slate-100 text-slate-400 cursor-not-allowed",
          ].join(" ")}
        >
          {step === questions.length - 1 ? "Generar mis prompts" : "Siguiente"}
          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
